import React, { useRef, useState } from "react";
import { updateLeague, setDiscordWebhook } from "../lib/api";
import { TimezoneSelect } from "./TimezoneSelect";
import { ManageStaffPanel } from "./ManageStaffPanel";

// Banner sizing — 4:1 aspect ratio, optimised for 1600×400 (max 2 MB raw).
const BANNER_W = 1600;
const BANNER_H = 400;
const BANNER_MAX_BYTES = 2 * 1024 * 1024;

export function LeagueEditModal({ open, league, onClose, onSaved, onToast }) {
  const DAYS = [
    { code: "mon", label: "MON" }, { code: "tue", label: "TUE" }, { code: "wed", label: "WED" },
    { code: "thu", label: "THU" }, { code: "fri", label: "FRI" }, { code: "sat", label: "SAT" },
    { code: "sun", label: "SUN" },
  ];
  const SLOTS = [
    { code: "morning", label: "Morning", hours: "06–12" },
    { code: "afternoon", label: "Afternoon", hours: "12–18" },
    { code: "evening", label: "Evening", hours: "18–22" },
    { code: "night", label: "Night", hours: "22–06" },
  ];
  const [form, setForm] = useState({
    name: league?.name || "",
    description: league?.description || "",
    spots_total: league?.spots_total || 8,
    pro_badge: league?.pro_badge || "",
    banner_url: league?.banner_url || "",
    pro_perks_text: (league?.pro_perks || []).join("\n"),
    discord_webhook: league?.discord_webhook || "",
  });
  const [availabilitySlots, setAvailabilitySlots] = useState(league?.availability?.slots || {});
  const [availabilityTz, setAvailabilityTz] = useState(league?.availability?.timezone || "UTC");
  const [bannerPreview, setBannerPreview] = useState(league?.banner_url || "");
  const bannerFileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Phase 59 — STAFF / SETTINGS tab toggle. SETTINGS is the original
  // form, STAFF surfaces the new co_admin/moderator management panel.
  const [tab, setTab] = useState("settings");
  const [allowCaptainsResults, setAllowCaptainsResults] = useState(
    league?.allow_captains_to_report_results !== false,
  );

  if (!open || !league) return null;
  const isPro = league.league_type === "PRO";
  const memberCount = league?.members?.length || 1;

  const toggleSlot = (day, slot) => {
    setAvailabilitySlots((prev) => {
      const cur = new Set(prev[day] || []);
      if (cur.has(slot)) cur.delete(slot); else cur.add(slot);
      return { ...prev, [day]: Array.from(cur) };
    });
  };

  const handleBannerFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > BANNER_MAX_BYTES) {
      onToast?.("Banner too large (max 2 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      // Down-scale to the target ratio so we don't store huge images.
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = BANNER_W;
        canvas.height = BANNER_H;
        const ctx = canvas.getContext("2d");
        // cover-fit: scale + crop to the 4:1 ratio.
        const srcRatio = img.width / img.height;
        const dstRatio = BANNER_W / BANNER_H;
        let sx = 0, sy = 0, sw = img.width, sh = img.height;
        if (srcRatio > dstRatio) {
          sw = img.height * dstRatio;
          sx = (img.width - sw) / 2;
        } else {
          sh = img.width / dstRatio;
          sy = (img.height - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, BANNER_W, BANNER_H);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setBannerPreview(dataUrl);
        setForm((f) => ({ ...f, banner_url: dataUrl }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = {
        name: form.name,
        description: form.description,
        spots_total: Number(form.spots_total),
        availability: { slots: availabilitySlots, timezone: availabilityTz },
        allow_captains_to_report_results: allowCaptainsResults,
      };
      if (isPro) {
        patch.pro_badge = form.pro_badge;
        patch.banner_url = form.banner_url;
        patch.pro_perks = form.pro_perks_text.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 12);
      }
      const updated = await updateLeague(league.league_id, patch);
      // Save the Discord webhook on its own endpoint (separate validation).
      if ((form.discord_webhook || "") !== (league.discord_webhook || "")) {
        await setDiscordWebhook(league.league_id, form.discord_webhook.trim());
      }
      onToast?.("✅ League updated.");
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={() => !busy && onClose?.()}
      data-testid="league-edit-backdrop"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="league-edit-modal"
        className={`glass rounded-3xl p-7 w-full max-w-lg max-h-[92vh] overflow-y-auto ${
          isPro ? "border-2 border-[#14b8a6]/60 shadow-[0_0_28px_rgba(250,204,21,0.35)]" : "neon-glow"
        }`}
      >
        <h3 className="font-display text-2xl grad-text mb-4">
          {isPro ? "👑 EDIT PRO LEAGUE" : "✏️ EDIT LEAGUE"}
        </h3>

        {/* Phase 59 — split into SETTINGS / STAFF / RULES tabs so we
            don't bloat the original form. */}
        <div className="flex gap-1.5 mb-4" data-testid="edit-tabs">
          {[
            { id: "settings", label: "SETTINGS", icon: "fa-sliders" },
            { id: "rules",    label: "RULES",    icon: "fa-scale-balanced" },
            { id: "staff",    label: "STAFF",    icon: "fa-user-shield" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`edit-tab-${t.id}`}
              className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg border ${
                tab === t.id
                  ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/55"
                  : "bg-black/40 text-white/65 border-white/15 hover:border-[#14b8a6]/40"
              }`}
            >
              <i className={`fas ${t.icon} mr-1`} />{t.label}
            </button>
          ))}
        </div>

        {tab === "staff" ? (
          <ManageStaffPanel league={league} onToast={onToast} />
        ) : tab === "rules" ? (
          <div data-testid="edit-rules-tab" className="space-y-4">
            <div className="glass rounded-2xl p-4 border border-[#14b8a6]/25">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowCaptainsResults}
                  onChange={(e) => setAllowCaptainsResults(e.target.checked)}
                  data-testid="edit-allow-captains-results"
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-bold text-white">
                    Allow team captains to report match results
                  </p>
                  <p className="text-[11px] text-white/55 mt-1 leading-snug">
                    When off, only league officials (owner / co-admin /
                    moderator) can submit results. Useful for
                    competitive tournaments where neutral scoring is
                    required.
                  </p>
                </div>
              </label>
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="edit-rules-save"
              className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-40"
            >
              <i className="fas fa-save mr-1" />SAVE RULES
            </button>
          </div>
        ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">NAME</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-name-input"
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">DESCRIPTION</label>
            <textarea
              rows="3"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="edit-desc-input"
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">
              TOTAL TEAM SPOTS · <span className="text-white/40">currently {memberCount} member{memberCount === 1 ? "" : "s"}</span>
            </label>
            <input
              type="number"
              min={Math.max(2, memberCount)}
              max={256}
              value={form.spots_total}
              onChange={(e) => setForm({ ...form, spots_total: e.target.value })}
              data-testid="edit-spots-total-input"
              className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
            />
          </div>

          {isPro && (
            <div className="rounded-2xl p-3 border border-[#14b8a6]/50 bg-[#14b8a6]/5 space-y-3">
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">👑 PRO PERKS</p>
              <div>
                <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">
                  🎨 LEAGUE BANNER · {BANNER_W}×{BANNER_H} px · max 2 MB
                </label>
                {bannerPreview && (
                  <div
                    data-testid="edit-banner-preview"
                    className="relative w-full rounded-xl overflow-hidden mb-2 border border-[#14b8a6]/40"
                    style={{ aspectRatio: `${BANNER_W} / ${BANNER_H}` }}
                  >
                    <img
                      src={bannerPreview}
                      alt="League banner preview"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={bannerFileRef}
                  onChange={handleBannerFile}
                  data-testid="edit-banner-upload-input"
                  className="hidden"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => bannerFileRef.current?.click()}
                    data-testid="edit-banner-upload-btn"
                    className="text-xs tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                  >
                    <i className="fas fa-image mr-1" />UPLOAD BANNER
                  </button>
                  {bannerPreview && (
                    <button
                      type="button"
                      onClick={() => { setBannerPreview(""); setForm((f) => ({ ...f, banner_url: "" })); }}
                      data-testid="edit-banner-clear-btn"
                      className="text-xs tracking-widest px-3 py-1.5 rounded-lg bg-black/30 text-white/60 border border-white/15 hover:text-white"
                    >
                      <i className="fas fa-trash mr-1" />CLEAR
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-white/40 mt-1">
                  Auto-cropped to 4:1. JPEG-compressed at 85% quality on upload.
                </p>
              </div>
              <div>
                <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">🛡 BADGE</label>
                <input
                  placeholder="e.g. ★"
                  maxLength={4}
                  value={form.pro_badge}
                  onChange={(e) => setForm({ ...form, pro_badge: e.target.value })}
                  data-testid="edit-pro-badge-input"
                  className="w-24 bg-black/40 border border-[#14b8a6]/40 rounded-2xl px-3 py-3 text-[#14b8a6] font-bold text-center"
                />
              </div>
              <textarea
                rows="4"
                placeholder="Perks — one per line"
                value={form.pro_perks_text}
                onChange={(e) => setForm({ ...form, pro_perks_text: e.target.value })}
                data-testid="edit-pro-perks-input"
                className="w-full bg-black/40 border border-[#14b8a6]/40 rounded-2xl px-4 py-3 text-white text-sm"
              />
            </div>
          )}

          <div data-testid="league-edit-availability" className="rounded-2xl p-3 border border-[#14b8a6]/40 bg-[#14b8a6]/5 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
                <i className="fas fa-calendar-check mr-1" />LEAGUE PLAY WINDOWS
              </p>
              <label className="flex items-center gap-2 text-[10px] text-white/60">
                <span>TZ</span>
                <TimezoneSelect
                  value={availabilityTz}
                  onChange={setAvailabilityTz}
                  dataTestid="league-edit-timezone"
                  className="bg-black/40 border border-white/15 rounded px-2 py-1 text-white text-[10px] max-w-[180px]"
                />
              </label>
            </div>
            <p className="text-[11px] text-white/55">
              Tick the slots when this league usually runs matches — teams use it to plan challenges.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[9px] tracking-widest text-white/40">
                    <th className="text-left py-1 pr-2">SLOT</th>
                    {DAYS.map((d) => (
                      <th key={d.code} className="text-center px-1">{d.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SLOTS.map((s) => (
                    <tr key={s.code} className="border-t border-white/5">
                      <td className="py-1.5 pr-2">
                        <span className="font-bold">{s.label}</span>
                        <span className="block text-[9px] text-white/40">{s.hours}</span>
                      </td>
                      {DAYS.map((d) => {
                        const checked = (availabilitySlots[d.code] || []).includes(s.code);
                        return (
                          <td key={d.code} className="text-center px-1">
                            <button
                              type="button"
                              onClick={() => toggleSlot(d.code, s.code)}
                              data-testid={`league-edit-slot-${d.code}-${s.code}`}
                              aria-pressed={checked}
                              className={`w-7 h-7 rounded-md border transition ${
                                checked
                                  ? "bg-[#14b8a6]/25 border-[#14b8a6] text-[#14b8a6]"
                                  : "bg-black/40 border-white/15 text-white/30 hover:border-[#14b8a6]/50"
                              }`}
                            >
                              {checked ? "✓" : ""}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl p-3 border border-[#5865F2]/40 bg-[#5865F2]/5">
            <label className="block text-[10px] tracking-widest text-[#9bb0ff] mb-1">
              <i className="fab fa-discord mr-1" /> DISCORD WEBHOOK
            </label>
            <input
              value={form.discord_webhook}
              onChange={(e) => setForm({ ...form, discord_webhook: e.target.value })}
              placeholder="https://discord.com/api/webhooks/..."
              data-testid="edit-discord-webhook-input"
              className="w-full bg-black/40 border border-[#5865F2]/40 rounded-2xl px-4 py-3 text-white text-sm"
            />
            <p className="text-[10px] text-white/50 mt-1.5">
              Members can ping this channel with their next match in one click. Empty = disabled.
            </p>
          </div>
        </div>
        )}

        {tab === "settings" && (
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button" onClick={onClose} disabled={busy}
            data-testid="edit-cancel-btn"
            className="btn-ghost rounded-2xl px-5 py-3 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit" disabled={busy}
            data-testid="edit-save-btn"
            className={`rounded-2xl px-6 py-3 font-bold tracking-widest text-xs disabled:opacity-50 ${
              isPro ? "bg-[#14b8a6] text-black hover:brightness-110" : "btn-neon"
            }`}
          >
            {busy ? "SAVING..." : "SAVE"}
          </button>
        </div>
        )}
      </form>
    </div>
  );
}
export default LeagueEditModal;
